import { useState } from 'react';
import { formatearCLP } from '@lexicore/shared';
import {
  IconoCerrar,
  IconoCopiar,
  IconoCheck,
  IconoWhatsapp,
  IconoCorreo,
  IconoDocumento,
} from '../../componentes/Iconos';
import { Cargando } from '../../componentes/UI';
import { useAvisos } from '../../contextos/AvisosContext';
import { useContactosFactura, useTextoCobroFactura, type Factura } from '../../lib/consultas';

interface Props {
  factura: Factura;
  alCerrar: () => void;
  alAbrirPagar?: () => void;
}

export function ModalGestionCobro({ factura, alCerrar, alAbrirPagar }: Props) {
  const avisos = useAvisos();
  const [copiado, setCopiado] = useState(false);

  const saldoPendiente =
    factura.saldoPendiente ??
    factura.situacion?.saldo ??
    Math.max(0, factura.montoTotal - (factura.montoPagado ?? factura.situacion?.montoPagado ?? 0));
  const diasMora = factura.diasMora ?? factura.situacion?.diasAtraso ?? 0;

  const textoCobroQuery = useTextoCobroFactura(factura.id);
  const contactosQuery = useContactosFactura(factura.id);

  const textoCarta =
    textoCobroQuery.data?.textoCobro ||
    factura.textoCobroSugerido ||
    `Estimado(a) encargado(a) de Finanzas y Proveedores de ${factura.organismo.nombre}, junto con saludar cordialmente, nos comunicamos en relación a la Factura N° ${factura.numero ?? 's/n'}${factura.numeroOrdenCompra ? ` asociada a la Orden de Compra N° ${factura.numeroOrdenCompra}` : ''} por un saldo de ${formatearCLP(saldoPendiente)}, la cual presenta ${diasMora > 0 ? `${diasMora} días de vencimiento` : 'vencimiento próximo'}. Agradeceremos informarnos el estado del decreto de pago o programación de transferencia bancaria. Atentamente, Departamento de Cobranzas.`;

  const handleCopiar = async () => {
    try {
      await navigator.clipboard.writeText(textoCarta);
      setCopiado(true);
      avisos.exito('Texto formal de cobranza copiado al portapapeles.');
      setTimeout(() => setCopiado(false), 3000);
    } catch {
      avisos.error('No se pudo copiar el texto automáticamente. Por favor selecciónalo manualmente.');
    }
  };

  const contactos = contactosQuery.data?.contactos ?? [];
  const whatsappUrlPrincipal =
    textoCobroQuery.data?.whatsappUrl ||
    (contactos[0]?.whatsappUrl ?? `https://wa.me/?text=${encodeURIComponent(textoCarta)}`);

  return (
    <div className="modal-overlay" onClick={alCerrar}>
      <div className="modal" style={{ maxWidth: 640, width: '100%' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 28,
                  height: 28,
                  borderRadius: 'var(--radius-sm)',
                  background: diasMora > 0 ? 'var(--accent-red-soft)' : 'var(--accent-blue-soft)',
                  color: diasMora > 0 ? 'var(--accent-red)' : 'var(--accent-blue)',
                }}
              >
                <IconoDocumento size={15} />
              </span>
              <h2 style={{ fontSize: 17, fontWeight: 700, margin: 0, color: 'var(--text-primary)' }}>
                Gestión de Cobranza Protocolar
              </h2>
            </div>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '4px 0 0 36px' }}>
              Plantilla formal chilena para Organismos Públicos y enlaces de contacto directo.
            </p>
          </div>
          <button type="button" className="btn-icono" onClick={alCerrar} aria-label="Cerrar modal">
            <IconoCerrar size={15} />
          </button>
        </div>

        <div style={{ padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Ficha Resumen */}
          <div
            style={{
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-md)',
              padding: '14px 16px',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 800, fontSize: 15, color: 'var(--text-primary)' }}>
                    Factura {factura.numero ? `#${factura.numero}` : 's/n'}
                  </span>
                  {diasMora > 0 ? (
                    <span
                      style={{
                        padding: '2px 8px',
                        borderRadius: 12,
                        background: 'var(--accent-red-soft)',
                        color: 'var(--accent-red)',
                        fontSize: 11,
                        fontWeight: 700,
                      }}
                    >
                      {diasMora} días vencida
                    </span>
                  ) : (
                    <span
                      style={{
                        padding: '2px 8px',
                        borderRadius: 12,
                        background: 'var(--accent-green-soft)',
                        color: 'var(--accent-green)',
                        fontSize: 11,
                        fontWeight: 700,
                      }}
                    >
                      Al día / Vigente
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginTop: 4 }}>
                  {factura.organismo.nombre}
                  {factura.organismo.rut && <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> ({factura.organismo.rut})</span>}
                </div>
                {factura.numeroOrdenCompra && (
                  <div style={{ fontSize: 11, color: 'var(--accent-blue)', marginTop: 2, fontWeight: 500 }}>
                    Orden de Compra: {factura.numeroOrdenCompra}
                  </div>
                )}
              </div>

              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Saldo Pendiente</div>
                <div className="td-mono" style={{ fontSize: 17, fontWeight: 800, color: 'var(--accent-yellow)' }}>
                  {formatearCLP(saldoPendiente)}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                  Total: {formatearCLP(factura.montoTotal)}
                </div>
              </div>
            </div>
          </div>

          {/* Plantilla Formal */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>
                Carta Protocolar Sugerida (Sector Público)
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  onClick={handleCopiar}
                  className="btn btn-secondary btn-sm"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5 }}
                >
                  {copiado ? <IconoCheck size={13} color="var(--accent-green)" /> : <IconoCopiar size={13} />}
                  <span>{copiado ? '¡Copiado!' : 'Copiar Texto'}</span>
                </button>
                <a
                  href={whatsappUrlPrincipal}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-sm"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: 11.5,
                    background: '#25D366',
                    color: '#fff',
                    borderColor: '#25D366',
                    textDecoration: 'none',
                  }}
                >
                  <IconoWhatsapp size={13} />
                  <span>Enviar por WhatsApp</span>
                </a>
              </div>
            </div>

            {textoCobroQuery.isLoading ? (
              <Cargando texto="Cargando texto formal..." />
            ) : (
              <div
                style={{
                  background: 'var(--bg-elevated)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 'var(--radius-md)',
                  padding: '12px 14px',
                  fontSize: 12,
                  lineHeight: 1.6,
                  color: 'var(--text-primary)',
                  whiteSpace: 'pre-wrap',
                  maxHeight: 150,
                  overflowY: 'auto',
                  fontFamily: 'inherit',
                }}
              >
                {textoCarta}
              </div>
            )}
          </div>

          {/* Contrapartes y Contactos Directos */}
          <div>
            <h3 style={{ fontSize: 12.5, fontWeight: 700, margin: '0 0 8px 0', color: 'var(--text-primary)' }}>
              Contrapartes del Organismo ({contactos.length})
            </h3>

            {contactosQuery.isLoading ? (
              <Cargando texto="Consultando contactos del organismo..." />
            ) : contactos.length === 0 ? (
              <div
                style={{
                  padding: '12px 16px',
                  borderRadius: 'var(--radius-sm)',
                  background: 'var(--bg-elevated)',
                  border: '1px solid var(--border-subtle)',
                  fontSize: 12,
                  color: 'var(--text-muted)',
                }}
              >
                No hay contactos registrados específicamente para este organismo comprador. Puedes copiar el texto y enviarlo a través de la oficina de partes o tesorería general.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 180, overflowY: 'auto' }}>
                {contactos.map((contacto) => (
                  <div
                    key={contacto.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '10px 12px',
                      borderRadius: 'var(--radius-sm)',
                      background: 'var(--bg-elevated)',
                      border: '1px solid var(--border-subtle)',
                      gap: 8,
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)' }}>
                          {contacto.nombre}
                        </span>
                        {contacto.esPrincipal && (
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              padding: '1px 6px',
                              borderRadius: 8,
                              background: 'var(--accent-blue-soft)',
                              color: 'var(--accent-blue)',
                            }}
                          >
                            Principal
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                        {contacto.cargo ?? 'Encargado de Finanzas / Pagos'}
                      </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {contacto.email && (
                        <a
                          href={`mailto:${contacto.email}?subject=${encodeURIComponent(
                            `Consulta Cobranza Factura ${factura.numero ?? ''} - ${factura.organismo.nombre}`
                          )}&body=${encodeURIComponent(textoCarta)}`}
                          className="btn btn-secondary btn-sm"
                          style={{ padding: '6px 8px', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11 }}
                          title={`Enviar email a ${contacto.email}`}
                        >
                          <IconoCorreo size={13} />
                          <span>Email</span>
                        </a>
                      )}

                      {contacto.whatsappUrl && (
                        <a
                          href={contacto.whatsappUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm"
                          style={{
                            padding: '6px 10px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            fontSize: 11,
                            background: '#25D366',
                            color: '#fff',
                            borderColor: '#25D366',
                            textDecoration: 'none',
                          }}
                          title="Abrir chat en WhatsApp"
                        >
                          <IconoWhatsapp size={13} />
                          <span>WhatsApp</span>
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer" style={{ padding: '16px 24px', borderTop: '1px solid var(--border-subtle)' }}>
          <button type="button" className="btn btn-secondary" onClick={alCerrar}>
            Cerrar
          </button>
          {alAbrirPagar && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                alCerrar();
                alAbrirPagar();
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: 'var(--accent-green)',
                borderColor: 'var(--accent-green)',
              }}
            >
              <IconoCheck size={14} />
              <span>Registrar Pago Directo</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
